import { ParsedUrlQueryInput } from 'querystring';
import { UrlObject } from 'url';

import { getSessionStorageItem, setSessionStorageItem } from '@revenuemarket/browser';

const UTM_SESSION_KEY = 'utm_params';

export interface UtmParams {
  [key: string]: string;
}

export function getUtmParams(): UtmParams | undefined {
  return getSessionStorageItem<UtmParams>(UTM_SESSION_KEY);
}

export function setUtmParams(params: UtmParams) {
  setSessionStorageItem(UTM_SESSION_KEY, params);
}

export function extractUtmParamsFromUrl(searchParams: URLSearchParams): UtmParams {
  const utmParams: UtmParams = {};
  searchParams.forEach((value, key) => {
    if (key.startsWith('utm_')) {
      utmParams[key] = value;
    }
  });
  return utmParams;
}

export function hasUtmSource(params: UtmParams): boolean {
  return 'utm_source' in params && params.utm_source.length > 0;
}

function isInternalUrl(url: string): boolean {
  if (url.startsWith('/')) {
    return true;
  }
  try {
    const parsedUrl = new URL(url, window.location.origin);
    return parsedUrl.origin === window.location.origin;
  } catch {
    return false;
  }
}

export function appendUtmToUrlString(url: string): string {
  if (typeof window === 'undefined') return url;
  if (!isInternalUrl(url)) return url;

  const storedUtm = getUtmParams();
  if (!storedUtm || !hasUtmSource(storedUtm)) return url;

  try {
    const parsedUrl = new URL(url, window.location.origin);
    Object.entries(storedUtm).forEach(([key, value]) => {
      if (!parsedUrl.searchParams.has(key)) {
        parsedUrl.searchParams.set(key, value);
      }
    });
    return url.startsWith('/') ? parsedUrl.pathname + parsedUrl.search : parsedUrl.href;
  } catch {
    return url;
  }
}

export function appendUtmToUrlObject(urlObj: UrlObject): UrlObject {
  if (typeof window === 'undefined') return urlObj;

  const storedUtm = getUtmParams();
  if (!storedUtm || !hasUtmSource(storedUtm)) return urlObj;

  const existingQuery = urlObj.query || {};
  const queryObj: ParsedUrlQueryInput =
    typeof existingQuery === 'string'
      ? Object.fromEntries(new URLSearchParams(existingQuery))
      : { ...existingQuery };

  Object.entries(storedUtm).forEach(([key, value]) => {
    if (!(key in queryObj)) {
      queryObj[key] = value;
    }
  });

  return {
    ...urlObj,
    query: queryObj,
  };
}

export function appendUtmToUrl(url: string | UrlObject): string | UrlObject {
  if (typeof url === 'string') {
    return appendUtmToUrlString(url);
  }
  return appendUtmToUrlObject(url);
}
